import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'users';

// Action Types
export const types = {
  GET_ADMINS: createAsyncTypes(`${KEY}/GET_ADMINS`),
  GET_ADMIN: createAsyncTypes(`${KEY}/GET_ADMIN`),
  GET_PINCODE: createAsyncTypes(`${KEY}/GET_PINCODE`),
  CREATE_ADMIN: createAsyncTypes(`${KEY}/CREATE_ADMIN`),
  UPDATE_ADMIN: createAsyncTypes(`${KEY}/UPDATE_ADMIN`),
  DELETE_ADMIN: createAsyncTypes(`${KEY}/DELETE_ADMIN`),
  RESET_PINCODE: `${KEY}/RESET_PINCODE`,
  RESET_ERROR: `${KEY}/RESET_ERROR`,
  RESET_ACTION: `${KEY}/RESET_ACTION`
};

export interface AdminsState {
  readonly admins: AdminWithActivity[];
  readonly admin: Admin | null;
  readonly pinCode: string;
  readonly action: {
    readonly successful: boolean;
    readonly pending: boolean;
    readonly error: string | null;
  };
}

export type Role = 'MaintenanceAdmin' | 'ReadOnlyAdmin' | 'ClientAdmin' | 'GlobalAdmin';

export interface Admin {
  id: string;
  clientId: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  venueId: string;
  pinCode: string;
  resetPasswordToken: null | string;
  resetPasswordRequired: boolean;
  sendAlertEmails: boolean;
  role: Role;
}

export interface AdminWithActivity extends Admin {
  latestActivity: {
    actorId: string;
    event: string;
    createdAt: string;
  } | null;
}

export interface CreateAdminPayload {
  venueId?: string;
  email: string;
  password?: string;
  name: string;
  role: Role;
  sendAlertEmails: boolean;
  pinCode?: string;
}

export interface UpdateAdminPayload {
  venueId?: string;
  id: string;
  password?: string | null;
  name: string;
  role: Role;
  sendAlertEmails: boolean;
  email: string;
  pinCode?: string;
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface DeleteAdminRequest {
  type: typeof types.DELETE_ADMIN.REQUEST;
  payload: { id: string };
}

export interface DeleteAdminError {
  type: typeof types.DELETE_ADMIN.ERROR;
  payload: string;
}

export interface DeleteAdminPending {
  type: typeof types.DELETE_ADMIN.PENDING;
  payload: boolean;
}

export interface DeleteAdminSuccess {
  type: typeof types.DELETE_ADMIN.SUCCESS;
}

export interface UpdateAdminRequest {
  type: typeof types.UPDATE_ADMIN.REQUEST;
  payload: UpdateAdminPayload;
}

export interface UpdateAdminError {
  type: typeof types.UPDATE_ADMIN.ERROR;
  payload: string;
}

export interface UpdateAdminPending {
  type: typeof types.UPDATE_ADMIN.PENDING;
  payload: boolean;
}

export interface UpdateAdminSuccess {
  type: typeof types.UPDATE_ADMIN.SUCCESS;
}

export interface CreateAdminRequest {
  type: typeof types.CREATE_ADMIN.REQUEST;
  payload: CreateAdminPayload;
}

export interface CreateAdminError {
  type: typeof types.CREATE_ADMIN.ERROR;
  payload: string;
}

export interface CreateAdminPending {
  type: typeof types.CREATE_ADMIN.PENDING;
  payload: boolean;
}

export interface CreateAdminSuccess {
  type: typeof types.CREATE_ADMIN.SUCCESS;
}

export interface GetAdminsRequest {
  type: typeof types.GET_ADMINS.REQUEST;
}

export interface GetAdminsError {
  type: typeof types.GET_ADMINS.ERROR;
  payload: string;
}

export interface GetAdminsPending {
  type: typeof types.GET_ADMINS.PENDING;
  payload: boolean;
}

export interface GetAdminsSuccess {
  type: typeof types.GET_ADMINS.SUCCESS;
  payload: AdminWithActivity[];
}

export interface GetAdminRequest {
  type: typeof types.GET_ADMIN.REQUEST;
  payload: {
    id: string;
  };
}

export interface GetAdminError {
  type: typeof types.GET_ADMIN.ERROR;
  payload: string;
}

export interface GetAdminPending {
  type: typeof types.GET_ADMIN.PENDING;
  payload: boolean;
}

export interface GetAdminSuccess {
  type: typeof types.GET_ADMIN.SUCCESS;
  payload: Admin;
}

export interface GetValidPinCodeError {
  type: typeof types.GET_PINCODE.ERROR;
  payload: string;
}

export interface GetValidPinCodePending {
  type: typeof types.GET_PINCODE.PENDING;
  payload: boolean;
}

export interface GetValidPinCodeSuccess {
  type: typeof types.GET_PINCODE.SUCCESS;
  payload: string;
}

export type AdminsActionTypes =
  | ResetAction
  | DeleteAdminRequest
  | DeleteAdminSuccess
  | DeleteAdminError
  | DeleteAdminPending
  | UpdateAdminRequest
  | UpdateAdminSuccess
  | UpdateAdminError
  | UpdateAdminPending
  | CreateAdminRequest
  | CreateAdminSuccess
  | CreateAdminError
  | CreateAdminPending
  | GetAdminsRequest
  | GetAdminsSuccess
  | GetAdminsError
  | GetAdminsPending
  | GetAdminRequest
  | GetAdminSuccess
  | GetAdminError
  | GetAdminPending
  | GetValidPinCodeError
  | GetValidPinCodePending
  | GetValidPinCodeSuccess;
